
/**
 * TGD | Product journey rail
 * Visual progress only.
 * Does not modify LPO, pricing or cart data.
 */

(() => {
  'use strict';

  const PRODUCT_SELECTOR =
    'product-info[data-sales-status="buyable"]';

  const SIZES = [
    'xxs', 'xs', 's', 'm', 'l',
    'xl', 'xxl', '3xl', '4xl', '5xl'
  ];

  let controller = null;
  let retryTimer = null;

  function isVisible(element) {
    if (!element || !element.isConnected) {
      return false;
    }

    const rect = element.getBoundingClientRect();

    return rect.width > 0 && rect.height > 0;
  }

  function getAnchors(info) {
    const color = info.querySelector(
      '#tgd-color-selector .tgd-color-selector__title'
    );

    const quantity = [
      ...info.querySelectorAll('.tgd-tallas-titulo')
    ].find(element =>
      isVisible(element) &&
      /quantities by size/i.test(
        element.textContent || ''
      )
    );

    const customize = info.querySelector(
      '.tgd-customizer-entry__button'
    );

    const add = info.querySelector(
      'product-form button[name="add"]'
    );

    const anchors = [
      color,
      quantity,
      customize,
      add
    ];

    return anchors.every(isVisible)
      ? anchors
      : null;
  }

  function totalUnits(info) {
    return SIZES.reduce((sum, size) => {
      const field = info.querySelector(
        `[name="properties[_${size}_x]"]`
      );

      const amount = Number.parseInt(
        field?.value || '0',
        10
      );

      return sum + (
        Number.isFinite(amount)
          ? Math.max(0, amount)
          : 0
      );
    }, 0);
  }

  function hasSavedCustomization() {
    const color = document.getElementById(
      'tgd-selected-color-code'
    )?.value || '';

    const key =
      `tgd-customization-draft:${location.pathname}:${color}`;

    const raw =
      window.tgdCustomizationDraftStore?.[key];

    if (!raw) return false;

    try {
      const draft = typeof raw === 'string'
        ? JSON.parse(raw)
        : raw;

      return !!(
        draft?.savedAt &&
        Array.isArray(draft.layers) &&
        draft.layers.some(layer =>
          layer?.imageSrc &&
          layer?.originalFileName
        )
      );
    } catch (error) {
      return false;
    }
  }

  function setup(product, info, anchors) {
    const previousPosition = info.style.position;

    if (
      getComputedStyle(info).position === 'static'
    ) {
      info.style.position = 'relative';
    }

    const rail = document.createElement('div');

    rail.className = 'tgd-journey';
    rail.setAttribute('aria-hidden', 'true');

    const track = document.createElement('span');
    track.className = 'tgd-journey__track';

    const fill = document.createElement('span');
    fill.className = 'tgd-journey__fill';

    const dots = anchors.map((_, index) => {
      const dot = document.createElement('span');

      dot.className = 'tgd-journey__point';
      dot.textContent = String(index + 1);

      return dot;
    });

    rail.append(track, fill, ...dots);
    info.appendChild(rail);

    const button = anchors[3];
    const form = button.closest('form');

    let hovered = false;
    let submitting = false;
    let confirmed = false;
    let raf = 0;
    let pendingTimeout = null;
    let unsubscribeUpdate = null;
    let unsubscribeError = null;
    let disposed = false;

    function render() {
      if (disposed || !rail.isConnected) {
        return;
      }

      const currentAnchors = getAnchors(info);

      if (!currentAnchors) return;

      const rootTop =
        info.getBoundingClientRect().top;

      const coordinates = currentAnchors.map(
        element => {
          const box =
            element.getBoundingClientRect();

          return (
            box.top +
            box.height / 2 -
            rootTop
          );
        }
      );

      const start = coordinates[0];
      const end = coordinates[3];

      if (end <= start) return;

      const units = totalUnits(info);
      const saved = hasSavedCustomization();

      const baseStep =
        units === 0
          ? 1
          : saved
            ? 3
            : 2;

      const canPreview =
        units > 0 &&
        !button.disabled &&
        button.getAttribute('aria-disabled') !== 'true';

      const showFinal =
        confirmed ||
        submitting ||
        (hovered && canPreview);

      const step = showFinal
        ? 4
        : baseStep;

      rail.style.top = `${start}px`;

      rail.style.height =
        `${end - start}px`;

      dots.forEach((dot, index) => {
        dot.style.top =
          `${coordinates[index] - start}px`;

        dot.classList.toggle(
          'is-complete',
          index < step
        );
      });

      fill.style.height =
        `${
          Math.max(
            0,
            coordinates[step - 1] - start
          )
        }px`;
    }

    function scheduleRender() {
      if (disposed) return;

      cancelAnimationFrame(raf);

      raf = requestAnimationFrame(render);
    }

    function releasePending() {
      submitting = false;

      if (pendingTimeout !== null) {
        clearTimeout(pendingTimeout);
        pendingTimeout = null;
      }
    }

    function onInput(event) {
      const target = event.target;

      if (
        target?.matches?.(
          'input[name^="properties[_"][name$="_x]"]'
        )
      ) {
        confirmed = false;
        scheduleRender();
      }
    }

    function onClick(event) {
      const target = event.target;

      if (!target?.closest) return;

      if (
        target.closest(
          '[data-tgd-customizer-save]'
        )
      ) {
        confirmed = false;

        setTimeout(
          scheduleRender,
          0
        );
      }

      if (
        target.closest('#tgd-color-selector')
      ) {
        confirmed = false;

        requestAnimationFrame(
          scheduleRender
        );
      }
    }

    function onEnter() {
      hovered = true;
      scheduleRender();
    }

    function onLeave() {
      hovered = false;
      scheduleRender();
    }

    function onSubmit(event) {
      if (
        event.target !== form ||
        totalUnits(info) <= 0
      ) {
        return;
      }

      confirmed = false;
      submitting = true;

      scheduleRender();

      if (pendingTimeout !== null) {
        clearTimeout(pendingTimeout);
      }

      pendingTimeout = setTimeout(() => {
        pendingTimeout = null;

        if (submitting && !confirmed) {
          submitting = false;
          hovered = false;
          scheduleRender();
        }
      }, 20000);
    }

    function isThisProductEvent(event) {
      if (
        event?.source !== 'product-form'
      ) {
        return false;
      }

      const thisVariant = form?.querySelector(
        '[name="id"]'
      )?.value;

      return (
        !thisVariant ||
        !event.productVariantId ||
        String(thisVariant) ===
          String(event.productVariantId)
      );
    }

    function onCartUpdate(event) {
      if (!isThisProductEvent(event)) {
        return;
      }

      if (
        !event.cartData ||
        event.cartData.status
      ) {
        return;
      }

      releasePending();

      confirmed = true;
      scheduleRender();
    }

    function onCartError(event) {
      if (!isThisProductEvent(event)) {
        return;
      }

      releasePending();

      confirmed = false;
      hovered = false;

      scheduleRender();
    }

    function onPageShow(event) {
      if (!event.persisted) return;

      releasePending();

      confirmed = false;
      hovered = false;

      scheduleRender();
    }

    info.addEventListener(
      'input',
      onInput
    );

    info.addEventListener(
      'change',
      onInput
    );

    document.addEventListener(
      'click',
      onClick
    );

    if (form) {
      form.addEventListener(
        'submit',
        onSubmit,
        true
      );
    }

    button.addEventListener(
      'mouseenter',
      onEnter
    );

    button.addEventListener(
      'mouseleave',
      onLeave
    );

    window.addEventListener(
      'blur',
      onLeave
    );

    window.addEventListener(
      'resize',
      scheduleRender
    );

    window.addEventListener(
      'pageshow',
      onPageShow
    );

    const resizeObserver =
      new ResizeObserver(scheduleRender);

    resizeObserver.observe(info);

    anchors.forEach(anchor => {
      resizeObserver.observe(anchor);
    });

    const domObserver =
      new MutationObserver(scheduleRender);

    domObserver.observe(info, {
      subtree: true,
      childList: true
    });

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined'
    ) {
      unsubscribeUpdate = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        onCartUpdate
      );

      unsubscribeError = subscribe(
        PUB_SUB_EVENTS.cartError,
        onCartError
      );
    }

    render();

    return {
      root: product,
      refresh: scheduleRender,

      destroy() {
        disposed = true;

        releasePending();
        cancelAnimationFrame(raf);

        resizeObserver.disconnect();
        domObserver.disconnect();

        info.removeEventListener(
          'input',
          onInput
        );

        info.removeEventListener(
          'change',
          onInput
        );

        document.removeEventListener(
          'click',
          onClick
        );

        if (form) {
          form.removeEventListener(
            'submit',
            onSubmit,
            true
          );
        }

        button.removeEventListener(
          'mouseenter',
          onEnter
        );

        button.removeEventListener(
          'mouseleave',
          onLeave
        );

        window.removeEventListener(
          'blur',
          onLeave
        );

        window.removeEventListener(
          'resize',
          scheduleRender
        );

        window.removeEventListener(
          'pageshow',
          onPageShow
        );

        if (unsubscribeUpdate) {
          unsubscribeUpdate();
        }

        if (unsubscribeError) {
          unsubscribeError();
        }

        rail.remove();

        info.style.position =
          previousPosition;
      }
    };
  }

  function tryInitialize() {
    const product = document.querySelector(
      PRODUCT_SELECTOR
    );

    if (!product) {
      return true;
    }

    if (
      controller?.root === product &&
      product.isConnected
    ) {
      return true;
    }

    if (controller) {
      controller.destroy();
      controller = null;
    }

    const info = product.querySelector(
      '.product__info-container'
    );

    if (!info) return false;

    const anchors = getAnchors(info);

    if (!anchors) return false;

    controller = setup(
      product,
      info,
      anchors
    );

    window.tgdJourney = controller;

    return true;
  }

  function initializeWithRetry() {
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
    }

    let attempt = 0;

    function run() {
      if (tryInitialize()) return;

      if (attempt++ < 40) {
        retryTimer = setTimeout(
          run,
          250
        );
      }
    }

    run();
  }

  document.addEventListener(
    'shopify:section:load',
    initializeWithRetry
  );

  document.addEventListener(
    'shopify:section:unload',
    () => {
      if (
        controller &&
        !controller.root.isConnected
      ) {
        controller.destroy();
        controller = null;

        delete window.tgdJourney;
      }
    }
  );

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initializeWithRetry,
      { once: true }
    );
  } else {
    initializeWithRetry();
  }
})();
